/** Central settings store. Every mutation reports what work it invalidates. */

import { DEFAULT_CUSTOM_RULE, type RuleKey } from './rules';

/** What has to be redone when a setting changes, in increasing order of cost. */
export type Invalidation = 'render' | 'stain' | 'shader' | 'volume';

export interface SourceSettings {
  /** Frame width of the volume (x). */
  width: number;
  /** Frame height of the volume (y). */
  height: number;
  /** Number of layers held in the volume (t). */
  layers: number;
  /**
   * 'even' seeks to evenly spaced timestamps, giving an exactly uniform t axis over
   * the whole clip; 'sequential' plays the clip and grabs presented frames, which is
   * needed for live sources but can drop frames. 'auto' picks 'even' when the clip
   * length is known.
   */
  captureMode: 'auto' | 'even' | 'sequential';
  /** Seconds between captures in sequential mode (0 = every presented frame). */
  captureInterval: number;
  /** Drop frames that are near-identical to the previous capture. */
  skipDuplicates: boolean;
}

export interface RuleSettings {
  key: RuleKey;
  compare: number;
  stride: number;
  blur: number;
  gain: number;
  bias: number;
  threshold: number;
  softness: number;
  decay: number;
  meanRate: number;
  gamma: number;
  colormap: number;
  sourceColor: boolean;
  paramA: number;
  paramB: number;
  paramC: number;
  customSrc: string;
}

export interface PeelSettings {
  playing: boolean;
  /** Playhead in layer units. */
  head: number;
  /** Layers per second. */
  speed: number;
  direction: 1 | -1;
  loop: 'loop' | 'once' | 'pingpong';
  /**
   * 'consume' starts from a solid block and strips layers off the exposed face as
   * they play, so the block shrinks toward the final frame. 'accumulate' does the
   * reverse: the block is built up behind the playhead as it advances.
   */
  mode: 'consume' | 'accumulate';
  /** Thickness of the intact block, in layers. */
  slabDepth: number;
  /** How many peeled layers linger as a trace. */
  traceDepth: number;
  /** Density of the trace relative to an intact layer. */
  traceAlpha: number;
  /** Per-layer falloff of the trace away from the playhead. */
  traceFade: number;
  /** 0 = the trace is a ghost of the footage, 1 = only its stain remains. */
  traceStain: number;
  /** Shrink the box to the visible slab instead of always drawing the full extent. */
  fitBox: boolean;
  anchor: 'center' | 'front' | 'back';
}

export interface LookSettings {
  colorMode: number;
  densityMode: number;
  density: number;
  stainMix: number;
  stainGlow: number;
  cutoff: number;
  frontOpacity: number;
  depthShade: number;
  trilinear: boolean;
  autoSteps: boolean;
  steps: number;
  depthScale: number;
  exposure: number;
  gamma: number;
  tonemap: number;
  background: string;
  backgroundFalloff: number;
  vignette: number;
  wire: boolean;
  wireAlpha: number;
  wireColor: string;
  jitter: number;
  accumulate: boolean;
  maxSamples: number;
  resolutionScale: number;
}

export interface CameraSettings {
  fov: number;
  autoRotate: boolean;
  autoRotateSpeed: number;
}

export interface Settings {
  source: SourceSettings;
  rule: RuleSettings;
  peel: PeelSettings;
  look: LookSettings;
  camera: CameraSettings;
}

export function defaultSettings(): Settings {
  return {
    source: {
      width: 256,
      height: 144,
      layers: 192,
      captureMode: 'auto',
      captureInterval: 0,
      skipDuplicates: false,
    },
    rule: {
      key: 'diff',
      compare: 0,
      stride: 1,
      blur: 0,
      gain: 4,
      bias: 0,
      threshold: 0.06,
      softness: 0.02,
      decay: 0.94,
      meanRate: 0.04,
      gamma: 1,
      colormap: 0,
      sourceColor: false,
      paramA: 0.5,
      paramB: 0.5,
      paramC: 0.5,
      customSrc: DEFAULT_CUSTOM_RULE,
    },
    peel: {
      playing: true,
      head: 0,
      speed: 24,
      direction: 1,
      loop: 'loop',
      mode: 'consume',
      slabDepth: 512,
      traceDepth: 512,
      traceAlpha: 0.07,
      traceFade: 0,
      traceStain: 1,
      fitBox: false,
      anchor: 'back',
    },
    look: {
      colorMode: 0,
      densityMode: 0,
      density: 8,
      stainMix: 0.85,
      stainGlow: 0,
      cutoff: 0.02,
      frontOpacity: 0.92,
      depthShade: 0.35,
      trilinear: false,
      autoSteps: true,
      steps: 384,
      depthScale: 1.15,
      exposure: 1,
      gamma: 2.2,
      tonemap: 2,
      background: '#07070a',
      backgroundFalloff: 3.2,
      vignette: 0.35,
      wire: true,
      wireAlpha: 0.22,
      wireColor: '#b9c6ff',
      jitter: 1,
      accumulate: true,
      maxSamples: 48,
      resolutionScale: 1,
    },
    camera: {
      fov: 38,
      autoRotate: false,
      autoRotateSpeed: 0.08,
    },
  };
}

/** Which recomputation each settings group triggers. Longest prefix wins. */
const INVALIDATION_RULES: Array<[string, Invalidation]> = [
  ['source.', 'volume'],
  ['rule.customSrc', 'shader'],
  ['rule.', 'stain'],
  ['peel.', 'render'],
  ['look.', 'render'],
  ['camera.', 'render'],
];

export function invalidationFor(path: string): Invalidation {
  let best: Invalidation = 'render';
  let bestLen = -1;
  for (const [prefix, inv] of INVALIDATION_RULES) {
    if (path.startsWith(prefix) && prefix.length > bestLen) {
      best = inv;
      bestLen = prefix.length;
    }
  }
  return best;
}

export interface ChangeEvent {
  path: string;
  value: unknown;
  invalidation: Invalidation;
}

type Listener = (event: ChangeEvent) => void;

export class Store {
  readonly settings: Settings = defaultSettings();
  private readonly listeners = new Set<Listener>();
  private readonly pathListeners = new Map<string, Set<() => void>>();

  get<T = unknown>(path: string): T {
    const parts = path.split('.');
    let node: unknown = this.settings;
    for (const part of parts) {
      if (node === null || typeof node !== 'object') return undefined as T;
      node = (node as Record<string, unknown>)[part];
    }
    return node as T;
  }

  set(path: string, value: unknown, silent = false): void {
    const parts = path.split('.');
    const last = parts.pop();
    if (!last) throw new Error(`Invalid settings path: "${path}"`);
    let node: Record<string, unknown> = this.settings as unknown as Record<string, unknown>;
    for (const part of parts) {
      const next = node[part];
      if (next === null || typeof next !== 'object') {
        throw new Error(`Invalid settings path: "${path}"`);
      }
      node = next as Record<string, unknown>;
    }
    if (node[last] === value) return;
    node[last] = value;
    if (silent) return;
    this.emit({ path, value, invalidation: invalidationFor(path) });
  }

  /** Applies several values, emitting the strongest single invalidation once per path. */
  patch(values: Record<string, unknown>): void {
    for (const [path, value] of Object.entries(values)) {
      this.set(path, value);
    }
  }

  emit(event: ChangeEvent): void {
    for (const listener of this.listeners) listener(event);
    const bound = this.pathListeners.get(event.path);
    if (bound) for (const fn of bound) fn();
    // Group-level subscribers (e.g. "rule") see every change beneath them.
    const group = event.path.split('.')[0];
    const groupBound = this.pathListeners.get(group);
    if (groupBound) for (const fn of groupBound) fn();
  }

  /** Notifies listeners without changing anything — used after external mutations. */
  touch(path: string): void {
    this.emit({ path, value: this.get(path), invalidation: invalidationFor(path) });
  }

  onChange(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onPath(path: string, listener: () => void): () => void {
    let set = this.pathListeners.get(path);
    if (!set) {
      set = new Set();
      this.pathListeners.set(path, set);
    }
    set.add(listener);
    return () => set?.delete(listener);
  }

  toJSON(): Settings {
    return JSON.parse(JSON.stringify(this.settings)) as Settings;
  }

  /** Merges a saved settings object in, keeping unknown/missing keys at their current value. */
  load(data: Partial<Settings>, skipPaths: string[] = []): void {
    const walk = (prefix: string, value: unknown) => {
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
          walk(prefix ? `${prefix}.${k}` : k, v);
        }
        return;
      }
      if (!prefix) return;
      if (skipPaths.some((p) => prefix === p || prefix.startsWith(`${p}.`))) return;
      if (this.get(prefix) === undefined) return;
      this.set(prefix, value);
    };
    walk('', data);
  }
}

export function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [0, 0, 0];
  const n = parseInt(m[1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}
